import { IonIcon, IonProgressBar, IonRouterLink, IonText } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { REFLECTION_TARGET } from "@/lib/constants";
import { checkIcon } from "@/lib/icons/@heroicons/react/16/solid";
import { arrowRightIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { queryOptions_reflection } from "@/lib/query/functions/reflection/all";
import { useMyProgression } from "@/lib/query/functions/user/progression";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";
import { cx } from "@/lib/style/cva.config";

export function ReflectionProgress({
	withAction = true,
	...props
}: React.ComponentProps<typeof IonRouterLink> & { withAction?: boolean }) {
	const { t } = useTranslation();
	const presentModal = useSelectorModalStore.use.present();
	const [myProgression] = useMyProgression();

	const query = useQuery({
		...queryOptions_reflection(),
		select: (data) => ({
			count:
				data?.filter(
					(question) =>
						question.reflectionChat?.status === "CHAT_COMPLETED" ||
						question.reflectionChat?.status === "FINISHED",
				).length ?? 0,
			target: data?.length ?? REFLECTION_TARGET,
		}),
	});
	const { count, target } = query.data ?? { count: 0, target: REFLECTION_TARGET };
	const hasCompleted = count >= target;

	return (
		<IonRouterLink
			data-attr="reflection.progress"
			{...(hasCompleted
				? {
						onClick: () =>
							presentModal(
								myProgression.reflection === "COMPLETED_ALL"
									? "reflectionAnalysisView"
									: "reflectionAnalysisGenerate",
							),
					}
				: {
						routerLink: Routes.ReflectionQuestion(),
						routerDirection: "none",
					})}
			{...props}
			className={cx(
				"border-brown-200 bg-brown-100 flex flex-col rounded-2xl border p-3 text-start",
				hasCompleted && "ion-activatable cursor-pointer",
				props.className,
			)}
		>
			<div className="flex items-center justify-between gap-2">
				<IonText className="text-brown-700 leading-5 font-bold">
					{t("reflection.progress.title")}
				</IonText>

				{withAction && (
					<div className="flex items-center justify-end gap-1">
						{hasCompleted && (
							<IonText className="text-brown-400 text-xs font-semibold">
								{myProgression.reflection === "COMPLETED_ALL"
									? t("reflection.progress.view-analysis")
									: t("reflection.progress.generate-analysis")}
							</IonText>
						)}
						<IonIcon icon={arrowRightIcon} className="text-brown-400 size-5" />
					</div>
				)}
			</div>

			<IonText className="text-brown-700 text-sm leading-5">
				{t("reflection.progress.text", {
					count,
					target,
				})}
			</IonText>

			<div className="relative isolate flex h-5 w-full items-center">
				<IonProgressBar value={count / target} className="!ion-bg-brown-200 h-2" />
				<div
					aria-checked={hasCompleted}
					className="bg-brown-200 absolute inset-y-0 right-0 z-10 grid size-5 place-content-center rounded-full text-transparent aria-checked:bg-orange-500 aria-checked:text-white"
				>
					<IonIcon icon={checkIcon} className="size-4" />
				</div>
			</div>
		</IonRouterLink>
	);
}
